import React from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { LogOut } from 'lucide-react';
import { useAuth } from '../../contexts/AuthContext';
import { USER_NAV_ITEMS, ADMIN_NAV_ITEMS } from './navItems';

/**
 * Authenticated left rail — uses the official Ascend blue (#002fa7) so the
 * brand is unmistakable the moment you sign in. White text on blue, lighter
 * lift-on-hover, and a crisp accent-bar + light-tint on the active route.
 *
 * The bottom-padding on the footer area is intentional: it clears the
 * CookieHub consent shield that sits at the viewport's bottom-left across
 * all breakpoints.
 */

const BRAND = '#002fa7';
const BRAND_HOVER = '#0142d4';   // ~10% lift; same hue family, more pop on hover
const BRAND_DARK = '#001d6a';    // pinned footer accent strip

const linkBase =
  'group flex items-center gap-3 px-3 py-2.5 text-sm font-medium transition-all duration-150 relative';

// Inactive state: 80% white so the active item stands out crisply.
const linkIdle =
  'text-white/80 hover:text-white hover:bg-white/10';

// Active: full-white text, slight white-tint background, and a 3px accent bar
// on the left edge to anchor the eye to the current page.
const linkActive =
  'text-white bg-white/15 before:content-[\'\'] before:absolute before:left-0 before:top-1.5 before:bottom-1.5 before:w-[3px] before:bg-white';

const NavLink = ({ to, icon: Icon, label, isActive, testId }) => (
  <Link
    to={to}
    title={label}
    aria-label={label}
    className={`${linkBase} ${isActive ? linkActive : linkIdle}`}
    data-testid={testId}
  >
    <Icon size={18} className="flex-shrink-0" strokeWidth={isActive ? 2.4 : 2} />
    <span className="hidden md:block truncate">{label}</span>
  </Link>
);

const Sidebar = () => {
  const { isAuthenticated, user, logout } = useAuth();
  const { pathname } = useLocation();
  const navigate = useNavigate();

  if (!isAuthenticated) return null;
  const isAdmin = user?.role === 'admin';

  const handleLogout = async () => {
    await logout();
    navigate('/');
  };

  return (
    <aside
      className="fixed left-0 bottom-0 w-52 z-40 hidden md:flex flex-col text-white shadow-[1px_0_0_rgba(255,255,255,0.06)]"
      style={{
        top: 'var(--header-h, 64px)',
        background: BRAND,
        // Subtle vertical gradient — top slightly brighter, bottom slightly
        // darker — gives the rail visual weight without looking gimmicky.
        backgroundImage: `linear-gradient(180deg, ${BRAND_HOVER} 0%, ${BRAND} 22%, ${BRAND} 78%, ${BRAND_DARK} 100%)`,
      }}
      data-testid="sidebar"
    >
      {/* Scrollable nav */}
      <nav className="flex-1 flex flex-col gap-1 px-2 py-4 overflow-y-auto">
        {USER_NAV_ITEMS.map(({ to, icon, label }) => {
          const isActive =
            pathname === to || (to !== '/dashboard' && pathname.startsWith(to));
          return (
            <NavLink
              key={to}
              to={to}
              icon={icon}
              label={label}
              isActive={isActive}
              testId={`sidebar-${label.toLowerCase().replace(/\s+/g, '-')}`}
            />
          );
        })}

        {isAdmin && (
          <>
            <div className="mt-5 mb-1 px-3 text-[10px] uppercase tracking-wider font-bold text-white/50 hidden lg:block">
              Admin
            </div>
            {ADMIN_NAV_ITEMS.map(({ to, icon, label }) => (
              <NavLink
                key={to}
                to={to}
                icon={icon}
                label={label}
                isActive={pathname === to}
                testId={`sidebar-admin-${label.toLowerCase()}`}
              />
            ))}
          </>
        )}
      </nav>

      {/* Pinned footer — bottom padding clears the CookieHub consent shield. */}
      <div className="border-t border-white/15 p-2 pb-16">
        <button
          type="button"
          onClick={handleLogout}
          className={`${linkBase} w-full text-white/80 hover:bg-white/10 hover:text-white`}
          data-testid="sidebar-logout"
        >
          <LogOut size={18} className="flex-shrink-0" />
          <span className="hidden lg:block truncate">Logout</span>
        </button>
      </div>
    </aside>
  );
};

export default Sidebar;
